<template>
  <div class="billingAddressForm">
    <div class="label mb-1">Credit Card Billing Address</div>
    <RadioGroup
      v-model="addressType"
      :options="addressOptions"
      class="addressOptions"
      horizontal
    >
      <template #option-current-content>
        <div class="text-small">
          {{ address.street }}
          <br />
          {{ address.city }}, {{ address.state }} {{ address.zip_code }}
        </div>
      </template>
      <template #option-new-content>
        <div class="text-small">Use different address</div>
      </template>
    </RadioGroup>
    <div v-if="addressType === 'new'" class="mt-1">
      <TextField
        v-model="formValues.street"
        name="street"
        label="Address"
        type="text"
        class="w-100"
        :error-message="errorMessages.street"
      />
      <div class="d-flex">
        <TextField
          v-model="formValues.city"
          name="city"
          label="City"
          type="text"
          style="width: 300px"
          class="mr-2"
          :error-message="errorMessages.city"
        />
        <div class="mr-2">
          <div class="label mb-1">State</div>
          <Select
            v-model="formValues.state"
            search
            class="stateInput"
            :class="{ 'is-invalid': errorMessages.state }"
            :options="STATE_OPTIONS"
          />
          <span v-if="errorMessages.state" class="text-danger error-text">{{
            errorMessages.state
          }}</span>
        </div>
        <TextField
          v-model="formValues.zip_code"
          name="zip_code"
          label="Zip Code"
          type="tel"
          style="width: 300px"
          class="mr-2"
          placeholder="Zip Code"
          :error-message="errorMessages.zip_code"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { STATE_OPTIONS } from "serviceshift-ui/shared/src/lib/stateOptions";
import { ref } from "vue";

import RadioGroup from "@/components/RadioGroup.vue";
import TextField from "@/components/TextField.vue";

const props = defineProps({
  address: {
    type: Object as () => {
      city: string;
      state: string;
      street: string;
      zip_code: string;
    },
    default: null
  }
});
const addressType = ref<"current" | "new">("current");
const errorMessages = ref({
  street: "",
  zip_code: "",
  city: "",
  state: ""
});
const formValues = ref({
  street: "",
  zip_code: "",
  city: "",
  state: ""
});

const addressOptions = [{ value: "current" }, { value: "new" }];

function clearErrors() {
  Object.keys(errorMessages.value).forEach((key) => {
    errorMessages.value[key] = "";
  });
}

async function getData() {
  clearErrors();
  const data = formValues.value;
  if (addressType.value === "current") return props.address;
  if (!data.street) {
    const error = "Address is required";
    errorMessages.value.street = error;
    throw error;
  }
  if (!data.city) {
    const error = "City is required";
    errorMessages.value.city = error;
    throw error;
  }
  if (!data.state) {
    const error = "State is required";
    errorMessages.value.state = error;
    throw error;
  }
  if (!data.zip_code) {
    const error = "Zip code is required";
    errorMessages.value.zip_code = error;
    throw error;
  }
  return data;
}

defineExpose({
  getData
});
</script>

<style lang="scss">
.billingAddressForm {
  .radio-group {
    width: 100%;
  }
  .label {
    margin-bottom: 3px !important;
  }
  .stateInput {
    input {
      height: 35px;
      box-shadow: 0 3px 6px 0px #9ca4aa;
    }
  }
  .addressOptions {
    display: flex;
    .group {
      flex: 1;
      &:not(:last-child) {
        margin-right: 20px;
      }
    }
  }
}
</style>
